import { cn } from '../../../shared/lib/utils';
import { ChevronRight } from 'lucide-react';

interface NavItem { label: string; href: string }

interface APGroup {
  name: string;
  hubHref: string;
  items: NavItem[];
}

const AP_GROUPS: APGroup[] = [
  {
    name: 'Procurement', hubHref: '#ap-procurement',
    items: [
      { label: 'Purchase Requisitions', href: '#purchase-requisitions' },
      { label: 'Purchase Orders', href: '#purchase-orders' },
      { label: 'Goods Receiving', href: '#goods-receiving' },
      { label: 'Supply Chain Tracker', href: '#ap-supply-chain' },
    ],
  },
  {
    name: 'Bills & Payments', hubHref: '#ap-bills',
    items: [
      { label: 'Bill Processing', href: '#bill-processing' },
      { label: 'AI Bill Scanner', href: '#bill-scanner' },
      { label: 'Payment Processing', href: '#payment-processing' },
      { label: 'Scheduled Payments', href: '#scheduled-payments' },
      { label: 'Recurring Bills', href: '#recurring-bills' },
    ],
  },
  {
    name: 'Vendors', hubHref: '#ap-vendors',
    items: [
      { label: 'Vendor Management', href: '#vendor-mgmt' },
      { label: 'Vendor Performance', href: '#vendor-performance' },
      { label: 'Vendor Statements', href: '#vendor-statements' },
      { label: 'Vendor Portal', href: '#vendor-portal' },
    ],
  },
  {
    name: 'Banking', hubHref: '#ap-banking',
    items: [
      { label: 'Bank Feeds', href: '#bank-feeds' },
      { label: 'Bank Reconciliation', href: '#bank-reconciliation' },
    ],
  },
  {
    name: 'Reports & Control', hubHref: '#ap-reports',
    items: [
      { label: 'AP Aging', href: '#ap-aging' },
      { label: 'AP Reports', href: '#ap-analytics' },
      { label: 'Expense Reports', href: '#expense-reports' },
      { label: 'Audit Trail', href: '#audit-trail' },
      { label: 'Consolidated AP', href: '#ap-consolidated' },
    ],
  },
];

/**
 * Find which group the current page belongs to
 */
function findGroup(currentPath: string): APGroup | null {
  return AP_GROUPS.find(g => g.hubHref === currentPath || g.items.some(i => i.href === currentPath)) || null;
}

/**
 * AP Sub-Navigation — shows breadcrumb + sibling tabs
 * Place this at the top of any AP page.
 */
export default function APSubNav() {
  const currentPath = window.location.hash.split('?')[0] || '#';
  const group = findGroup(currentPath);
  if (!group) return null;

  const currentItem = group.items.find(i => i.href === currentPath);
  // If on hub route, highlight first item
  const activeHref = currentItem ? currentPath : group.items[0]?.href;

  return (
    <div className="shrink-0 sticky top-0 z-30 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-4">
      <div className="flex items-center gap-4 -mb-px overflow-x-auto mobile-tabs-scroll">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 shrink-0 py-2.5">
          <a href={group.hubHref} className="text-[10px] font-bold text-slate-400 hover:text-violet-500 transition-colors">AP</a>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">{group.name}</span>
        </div>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 shrink-0" />

        {/* Sibling Tabs */}
        <div className="flex items-center gap-0.5 shrink-0">
        {group.items.map(item => {
          const isActive = item.href === activeHref;
          return (
            <a key={item.href} href={item.href}
              className={cn(
                "px-3 py-2.5 text-[11px] font-bold transition-all border-b-2 whitespace-nowrap",
                isActive
                  ? "text-violet-600 dark:text-violet-400 border-violet-500"
                  : "text-slate-400 border-transparent hover:text-slate-600 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600"
              )}>
              {item.label}
            </a>
          );
        })}
        </div>
      </div>
    </div>
  );
}
